import { useEffect, useRef, useState } from "react";
import { shallowEqual, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { useLayout } from "../../../_metronic/layout/core";
import { RootState } from "../../../setup";
import { UserRole } from "../../constants/roles";
import { showErrorAlert } from "../../helpers/alert";
import { UserModel } from "../../modules/auth/models/UserModel";
import { hasRole } from "../../utils/permissions";
import { CommunityMastermindForm } from "../community/components/CommunityMastermindForm";
import { CommunityOpenDiscussForm } from "../community/components/CommunityOpenDiscussForm";
import { CommunitySelector } from "../community/components/CommunitySelector";
import { CommunityFormEnum } from "../community/enums/CommunityFormEnum";
import { CommunityAdminHostSelector } from "./components/CommunityAdminHostSelector";
import { CommunityAdminHostModel } from "./models/CommunityAdminHostModel";
import { CommunityOpenDiscussCreate } from "../community/models/CommunityOpenDiscussCreate";
import { createAdminMastermindCircle, createAdminOpenDiscussion } from "../../services/communityService";
import { CommunityMastermindCreateModel } from "../community/models/CommunityMastermindCreateModel";
import { LoaderOverlay } from "../../hooks/loader/LoaderOverlay";

export const CommunityAdminFormPage = () => {
    const {config, setLayout} = useLayout()
    const restoreRef = useRef(config)
    const user = useSelector<RootState>(({auth}) => auth.user, shallowEqual) as UserModel;
    const history = useHistory();

    useEffect(() => {
        restoreRef.current = config
        setLayout({
        ...config,
            header: {...config.header, display: true},
            aside: {...config.aside, display: true},
            toolbar: {...config.toolbar, display: false},
            footer: {...config.footer, display: true},
        })

        allowed();

        return () => setLayout(restoreRef.current)
    }, []);

    const allowed = () => {
        const _isAdmin = hasRole(user, UserRole.ADMIN)
        if(!_isAdmin) {
            history.goBack();
            showErrorAlert({ title: 'Access Denied', message: 'You do not have permission to access this page.' });
        }
    }

    useEffect(() => {
        document.body.classList.add('admin-community-container')
        return () => document.body.classList.remove('admin-community-container')
    }, []);
    
    const [loading, setLoading] = useState(false);
    const [type, setType] = useState<CommunityFormEnum>(CommunityFormEnum.MASTERMIND_CIRCLE);
    const [host, setHost] = useState<CommunityAdminHostModel | null>(null);
    
    useEffect(() => {
        setType(CommunityFormEnum.OPEN_DISCUSSION);
    }, [host]);

    const _handleSubmitOpen = async (form: CommunityOpenDiscussCreate) => {
        if(!host)
            return;
        try {
            setLoading(true);
            const _tags = form.tags.map(t => t.value as string);
            await createAdminOpenDiscussion(host.id, form.topic, form.description, _tags);
            history.push('/admin/community');
        }
        finally {
            setLoading(false);
        }
    }

    const _handleSubmitCirlce = async (form: CommunityMastermindCreateModel) => {
        if(!host)
            return;
        try {
            setLoading(true);
            const _tags = form.tags.map(t => t.value as string);
            const _invites = form.invites.map(i => i.value.id) as number[];
            await createAdminMastermindCircle(host.id, form.topic, form.content, _tags, _invites, form.facilitator);
            history.push('/admin/community');
        }
        finally {
            setLoading(false);
        }
    }

    return (
        <>
            <LoaderOverlay visible={loading} message={'Loading...'} />
            <div className="community-form">
                <div className="d-flex flex-column" style={{gap: '10px'}}>
                    <h3>
                        Community
                    </h3>
                    <p>
                        You are among the world’s brightest minds. Here, the space is open for you to share, exchange, and grow—whether in open discussions or exclusive mastermind circles.
                    </p>
                </div>

                <CommunityAdminHostSelector 
                    hostId={host?.id ?? null}
                    onChangeHost={setHost}
                />

                <CommunitySelector 
                    onSelect={(id) => setType(id)}
                    selection={type}
                    subs={host?.subscriptionPlan?.title ?? ''}
                />
                {type === CommunityFormEnum.OPEN_DISCUSSION ? (
                    <CommunityOpenDiscussForm onSubmit={_handleSubmitOpen}/>
                ):(
                    <CommunityMastermindForm onSubmit={_handleSubmitCirlce}/>
                )}
            </div>
        </>
    )
}